'use client';

import { useTranslation } from 'react-i18next';
import type { CompanyCountryCode } from '@/lib/company-utils';
import { flagUrlForCountry } from '@/lib/country-flags';
import { cn } from '@/lib/utils';

/**
 * Small flag — optionally followed by the country name — saying whose
 * register a company is in. A bare city is ambiguous across the two
 * registers: there is a Cham in Bavaria and one in the canton of Zug.
 *
 * Sized in `em`, so it follows the surrounding text (the company header's
 * 9px/12px, a results-table cell's 14px) without breakpoints of its own. The
 * flag is the SVG from `public/media/flags/`, never a regional-indicator
 * emoji (Windows renders those as the letters "DE"). No client state, so it
 * is part of the server-rendered HTML.
 */
export function CompanyCountryFlag({
  countryCode,
  showName = false,
  className,
}: {
  countryCode: CompanyCountryCode;
  /** Render the localized country name after the flag. Without it the name
   *  is the flag's alt text and hover title. */
  showName?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const name = t(`companyDetail.countryName.${countryCode}`);
  const flagUrl = flagUrlForCountry(countryCode);
  return (
    <span
      className={cn('inline-flex shrink-0 items-center gap-1', className)}
      title={showName ? undefined : name}
    >
      {flagUrl && (
        <img
          src={flagUrl}
          alt={showName ? '' : name}
          aria-hidden={showName || undefined}
          className="h-[0.8em] w-[1.2em] shrink-0 rounded-[2px] object-cover"
        />
      )}
      {showName && <span>{name}</span>}
    </span>
  );
}
